import { useCallback, useMemo, useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { Input, Button, Dropdown, Table } from "antd";
import type { MenuProps } from "antd";
import Container from "@mui/material/Container";
import { ApolloError } from "@apollo/client";
import { Box, Divider, Typography } from "@mui/material";

import { useGettGritRankQuery } from "../../graphql/queries/getGRITRank.generated";
import { useGetMentorshipProgramsPaginatedQuery } from "../../graphql/queries/getMentorshipProgramsPaginated.generated";
import { useGetSpecialtiesByWorkspaceIdQuery } from "../../graphql/queries/getSpecialtiesByWorkspaceId.generated";
import { useGetLocationsByWorkspaceIdQuery } from "../../graphql/queries/getLocationsByWorkspaceId.generated";
import { useGetUserQuery } from "../../graphql/queries/getUser.generated";
import styles from "./grit.style";
import { useGetMentorshipProgramsQuery } from "../../graphql/queries/getMentorshipPrograms.generated";

const columns = [
  {
    title: "Participant",
    dataIndex: "user",
    key: "USER",
    width: 270,
    render: (text: string, record: any) => {
      const loggedUserIsBoth = record?.mentorEnabled && record?.menteeEnabled;
      const loggedUserIsMentor = record?.mentorEnabled;
      const loggedUserIsMentee = record?.menteeEnabled;

      let imgClassname = "profile-img-wrapper";

      switch (true) {
        case loggedUserIsBoth:
        case loggedUserIsMentee:
          imgClassname = "profile-img-wrapper orange";
          break;
        case loggedUserIsMentor:
          imgClassname = "profile-img-wrapper";
          break;
        default:
          imgClassname = "profile-img-wrapper";
          break;
      }

      return (
        <div className="mentorship-details-user-row">
          <div className={imgClassname}>
            <img
              src={
                record.profilePic ||
                process.env.PUBLIC_URL + "/assets/images/anon-profile-pic.svg"
              }
              alt="user-profile-pic"
              className="mentorship-details-profile-img"
            />
          </div>
          <span className="mentorship-details-table-text">{text}</span>
        </div>
      );
    },
  },
  {
    title: "Score",
    dataIndex: "score",
    key: "SCORE",
    render: (score: number) => (score > 999 ? score.toLocaleString() : score),
  },
  {
    title: "Mentorship Program",
    dataIndex: "program",
    key: "PROGRAM",
    render: (programs: string[]) => programs?.join(", ") || "Not Active",
  },
  {
    title: "Specialty",
    dataIndex: "specialty",
    key: "SPECIALTY",
  },
  {
    title: "Location",
    dataIndex: "workspaceLocation",
    key: "WORKSPACELOCATION",
  },
  {
    title: "Date Joined",
    dataIndex: "createdAt",
    key: "CREATEDAT",
  },
];

const Grit = () => {
  const { loggedUser, loggedUserData } = useAuth();

  const [workspaceId, setWorkspaceId] = useState<number>(0);
  const [selectedSpecialtyFilter, setSelectedSpecialtyFilter] = useState<
    string[]
  >([]);
  const [selectedLocationFilter, setSelectedLocationFilter] = useState<
    string[]
  >([]);
  const [selectedProgramFilter, setSelectedProgramFilter] = useState<string[]>(
    []
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [specialtyDropdownOpen, setSpecialtyDropdownOpen] = useState(false);
  const [locationDropdownOpen, setLocationDropdownOpen] = useState(false);
  const [programDropdownOpen, setProgramDropdownOpen] = useState(false);

  useEffect(() => {
    if (loggedUserData?.workspaces?.length) {
      setWorkspaceId(loggedUserData.workspaces[0].workspace.id);
    }
  }, [loggedUserData]);

  const handleOnGetUserCompleted = useCallback(
    (data: any) => {
      if (
        data?.users_by_pk?.isWorkspaceAdmin ||
        data?.users_by_pk?.isHighnoteAdmin
      ) {
        setSelectedLocationFilter([]);
      } else if (data.users_by_pk.workspaceLocation?.id) {
        const locationId = data.users_by_pk.workspaceLocation.id.toString();
        if (!selectedLocationFilter.includes(locationId)) {
          setSelectedLocationFilter([locationId]);
        }
      }
    },
    [selectedLocationFilter]
  );

  const handleOnErrorsGetUsers = useCallback((error: ApolloError) => {
    console.log("Get Users Error: ", error);
  }, []);

  const { data: userData } = useGetUserQuery({
    skip: !loggedUser?.uid,
    variables: {
      id: loggedUser?.uid,
    },
    errorPolicy: "none",
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
    notifyOnNetworkStatusChange: true,
    onError: handleOnErrorsGetUsers,
    onCompleted: handleOnGetUserCompleted,
  });

  const { data: specialtiesData } = useGetSpecialtiesByWorkspaceIdQuery({
    skip: !workspaceId,
    variables: {
      workspaceId: workspaceId,
    },
    errorPolicy: "all",
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
  });

  const { data: locationsData } = useGetLocationsByWorkspaceIdQuery({
    skip: !workspaceId,
    variables: {
      workspaceId: workspaceId,
    },
    errorPolicy: "all",
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
  });

  const handleOnCompletedGetMentorshipPrograms = useCallback((data: any) => {
    // console.log("Get Mentorship Programs data: ", data);
  }, []);

  const handleOnErrorsGetMentorshipPrograms = useCallback(
    (error: ApolloError) => {
      console.log("Get Mentorship Programs error: ", error);
    },
    []
  );

  const { data: programsData, loading: mentorshipProgramsLoading } =
    useGetMentorshipProgramsPaginatedQuery({
      skip: !workspaceId,
      variables: {
        workspaceId: workspaceId as never,
        offset: 0,
        limit: 1000,
      },
      fetchPolicy: "cache-and-network",
      nextFetchPolicy: "cache-first",
      errorPolicy: "none",
      notifyOnNetworkStatusChange: true,
      onCompleted: handleOnCompletedGetMentorshipPrograms,
      onError: handleOnErrorsGetMentorshipPrograms,
    });

  const gritFilter = useMemo(() => {
    const where: Record<string, any> = {};

    if (
      userData?.users_by_pk?.isWorkspaceAdmin &&
      !userData?.users_by_pk?.isHighnoteAdmin &&
      workspaceId
    ) {
      where.workspaceId = { _eq: workspaceId };
    }

    if (selectedLocationFilter.length > 0) {
      where.user = {
        ...where.user,
        workspaceLocationId: {
          _in: selectedLocationFilter.map((id) => parseInt(id)),
        },
      };
    }

    if (selectedSpecialtyFilter.length > 0) {
      where.specialtyId = { _in: selectedSpecialtyFilter };
    }

    if (selectedProgramFilter.length > 0) {
      where._or = [
        {
          user: {
            mentorshipsAsMentor: {
              mentorshipProgramId: { _in: selectedProgramFilter },
            },
          },
        },
        {
          user: {
            mentorshipsAsMentee: {
              mentorshipProgramId: { _in: selectedProgramFilter },
            },
          },
        },
      ];
    }

    if (searchTerm) {
      where.user = {
        ...where.user,

        _or: [
          { fullName: { _ilike: `%${searchTerm}%` } },
          { profession: { _ilike: `%${searchTerm}%` } },
        ],
      };
    }

    return where;
  }, [
    selectedSpecialtyFilter,
    selectedLocationFilter,
    selectedProgramFilter,
    searchTerm,
    userData?.users_by_pk?.isWorkspaceAdmin,
    userData?.users_by_pk?.isHighnoteAdmin,
    userData?.users_by_pk?.workspaceLocation?.id,
    workspaceId,
  ]);

  const { data: gritRankData, loading } = useGettGritRankQuery({
    skip: !loggedUser?.uid || !userData,
    variables: {
      where: gritFilter,
    },
    fetchPolicy: "network-only",
    errorPolicy: "all",
    nextFetchPolicy: "cache-first",
  });

  const handleOnSelectSpecialtyFilter = useCallback(
    (args: { selectedKeys: string[] }) => {
      const { selectedKeys } = args;

      if (selectedKeys.includes("specialty-all")) {
        setSelectedSpecialtyFilter([]);
        return;
      }

      setSelectedSpecialtyFilter(selectedKeys);
    },
    []
  );

  const handleOnSelectLocationFilter = useCallback(
    (args: { selectedKeys: string[] }) => {
      const { selectedKeys } = args;

      if (selectedKeys.includes("location-all")) {
        setSelectedLocationFilter([]);
        return;
      }

      setSelectedLocationFilter(selectedKeys);
    },
    []
  );

  const handleOnSelectProgramFilter = useCallback(
    (args: { selectedKeys: string[] }) => {
      const { selectedKeys } = args;

      if (selectedKeys.includes("program-all")) {
        setSelectedProgramFilter([]);
        return;
      }

      setSelectedProgramFilter(selectedKeys);
    },
    []
  );

  const [specialtyOptions, specialtyDropdownLabel] = useMemo(() => {
    if (!specialtiesData?.specialties) return [[], "Specialty"];

    const items: MenuProps["items"] = [
      {
        key: "specialty-all",
        label: (
          <div>
            <input
              type="checkbox"
              checked={selectedSpecialtyFilter.length === 0}
              readOnly
            />
            <span style={{ marginLeft: 8 }}>All Specialties</span>
          </div>
        ),
      },
      ...specialtiesData.specialties.map((specialty) => ({
        key: specialty.id,
        label: (
          <div>
            <input
              type="checkbox"
              checked={selectedSpecialtyFilter.includes(specialty.id)}
              readOnly
            />
            <span style={{ marginLeft: 8 }}>{specialty.name}</span>
          </div>
        ),
      })),
    ];

    const label = selectedSpecialtyFilter.length
      ? `Specialty (${selectedSpecialtyFilter.length})`
      : "Specialty";

    return [items, label];
  }, [specialtiesData, selectedSpecialtyFilter]);

  const [locationOptions, locationDropdownLabel] = useMemo(() => {
    if (!locationsData?.workspaceLocations) {
      return [[], "Location"];
    }

    const items: MenuProps["items"] = [
      {
        key: "location-all",
        label: (
          <div>
            <input
              type="checkbox"
              checked={selectedLocationFilter.length === 0}
              readOnly
            />
            <span style={{ marginLeft: 8 }}>All Locations</span>
          </div>
        ),
      },
      ...locationsData.workspaceLocations.map((location) => ({
        key: location.id.toString(),
        label: (
          <div>
            <input
              type="checkbox"
              checked={selectedLocationFilter.includes(location.id.toString())}
              readOnly
            />
            <span style={{ marginLeft: 8 }}>{location.name}</span>
          </div>
        ),
      })),
    ];

    const label = selectedLocationFilter.length
      ? `Location (${selectedLocationFilter.length})`
      : "Location";

    return [items, label];
  }, [locationsData, selectedLocationFilter]);

  const [programOptions, programDropdownLabel] = useMemo(() => {
    if (!programsData?.mentorshipProgram) return [[], "Program"];

    const items: MenuProps["items"] = [
      {
        key: "program-all",
        label: (
          <div>
            <input
              type="checkbox"
              checked={selectedProgramFilter.length === 0}
              readOnly
            />
            <span style={{ marginLeft: 8 }}>All Programs</span>
          </div>
        ),
      },
      ...programsData.mentorshipProgram.map((program) => ({
        key: program.id,
        label: (
          <div>
            <input
              type="checkbox"
              checked={selectedProgramFilter.includes(program.id.toString())}
              readOnly
            />
            <span style={{ marginLeft: 8 }}>{program.name}</span>
          </div>
        ),
      })),
    ];

    const label = selectedProgramFilter.length
      ? `Mentorship Program (${selectedProgramFilter.length})`
      : "Mentorship Program";

    return [items, label];
  }, [programsData, selectedProgramFilter]);

  const dataSource = useMemo(() => {
    if (!gritRankData?.grit) return [];

    const top5Scores = gritRankData.grit.slice(0, 5).map((item) => item.score);

    return gritRankData.grit.map((item, index) => {
      const date = new Date(item?.user?.createdAt);
      const formattedCreatedAt = date.toLocaleDateString();

      const allPrograms = [
        ...(item.user?.mentorshipsAsMentor?.map(
          (m) => m.mentorshipProgram?.name
        ) || []),
        ...(item.user?.mentorshipsAsMentee?.map(
          (m) => m.mentorshipProgram?.name
        ) || []),
      ].filter(Boolean);

      const uniquePrograms = Array.from(new Set(allPrograms));

      return {
        key: item.user?.id || index,
        user: item.user?.fullName || "Unknown",
        profilePic: item.user?.profilePhotoLink,
        mentorEnabled: item.user?.mentorEnabled,
        menteeEnabled: item.user?.menteeEnabled,
        score: item.score,
        isTop5: top5Scores.includes(item.score),
        specialty: item.specialty?.name,
        program: uniquePrograms,
        workspaceLocation: item.user?.workspaceLocation?.abbreviation,
        createdAt: formattedCreatedAt,
      };
    });
  }, [gritRankData]);

  return (
    <Container component="main" maxWidth={false}>
      <Box
        sx={{
          marginTop: "24px",
          marginBottom: "24px",
          width: "100%",
          maxWidth: "100%",
          flexWrap: "wrap",
        }}
      >
        <Typography sx={styles.gritTitle} variant="h1">
          GRIT Leaderboard
        </Typography>
      </Box>

      <Divider sx={styles.divider} />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          maxWidth: "100%",
        }}
      >
        <Box sx={styles.filterContainer}>
          <Input
            placeholder="Search participants..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: 300 }}
            prefix={
              <img
                src={`${process.env.PUBLIC_URL}/assets/images/searchbar-icon.svg`}
                alt="Search"
                style={styles.searchIcon}
              />
            }
          />
          <Dropdown
            menu={{
              items: programOptions,
              selectable: true,
              multiple: true,
              onSelect: handleOnSelectProgramFilter,
              onDeselect: handleOnSelectProgramFilter,
              selectedKeys: selectedProgramFilter,
            }}
            arrow
            trigger={["click"]}
            open={programDropdownOpen}
            onOpenChange={(
              open: boolean,
              info: { source: "trigger" | "menu" }
            ) => {
              if (info.source === "trigger" && open === false) {
                setProgramDropdownOpen(false);
              }
            }}
          >
            <Button
              onClick={() => {
                setProgramDropdownOpen((previousValue) => !previousValue);
              }}
            >
              {programDropdownLabel}
              <img
                src={
                  process.env.PUBLIC_URL +
                  "/assets/images/black-chevron-down.svg"
                }
                style={{ width: 12, height: 12, marginLeft: 8 }}
                alt="dropdown icon"
              />
            </Button>
          </Dropdown>

          <Dropdown
            menu={{
              items: specialtyOptions,
              selectable: true,
              multiple: true,
              onSelect: handleOnSelectSpecialtyFilter,
              onDeselect: handleOnSelectSpecialtyFilter,
              selectedKeys: selectedSpecialtyFilter,
            }}
            arrow
            trigger={["click"]}
            open={specialtyDropdownOpen}
            onOpenChange={(
              open: boolean,
              info: { source: "trigger" | "menu" }
            ) => {
              if (info.source === "trigger" && open === false) {
                setSpecialtyDropdownOpen(false);
              }
            }}
          >
            <Button
              onClick={() => {
                setSpecialtyDropdownOpen((previousValue) => !previousValue);
              }}
            >
              {specialtyDropdownLabel}
              <img
                src={
                  process.env.PUBLIC_URL +
                  "/assets/images/black-chevron-down.svg"
                }
                style={{ width: 12, height: 12, marginLeft: 8 }}
                alt="dropdown icon"
              />
            </Button>
          </Dropdown>

          <Dropdown
            menu={{
              items: locationOptions,
              selectable: true,
              multiple: true,
              onSelect: handleOnSelectLocationFilter,
              onDeselect: handleOnSelectLocationFilter,
              selectedKeys: selectedLocationFilter,
            }}
            arrow
            trigger={["click"]}
            open={locationDropdownOpen}
            onOpenChange={(
              open: boolean,
              info: { source: "trigger" | "menu" }
            ) => {
              if (info.source === "trigger" && open === false) {
                setLocationDropdownOpen(false);
              }
            }}
          >
            <Button
              onClick={() => {
                setLocationDropdownOpen((previousValue) => !previousValue);
              }}
            >
              {locationDropdownLabel}
              <img
                src={
                  process.env.PUBLIC_URL +
                  "/assets/images/black-chevron-down.svg"
                }
                style={{ width: 12, height: 12, marginLeft: 8 }}
                alt="dropdown icon"
              />
            </Button>
          </Dropdown>
        </Box>

        <Box sx={[styles.tableContainer, { width: "100%" }]}>
          <Table
            className="grit-table"
            dataSource={dataSource}
            columns={columns}
            loading={loading}
            scroll={{ x: true }}
            pagination={{
              position: ["bottomCenter"],
            }}
          />
        </Box>
      </Box>
    </Container>
  );
};

export default Grit;
